import { useState, useEffect } from "react";
import FeedbackControl from "./FeedbackControl";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtMonYear(iso) {
  const [yr, mo] = iso.split('-');
  return `${MONTHS[parseInt(mo, 10) - 1]} ${yr}`;
}

function fmtMonDayYear(iso) {
  const [yr, mo, dy] = iso.split('-');
  return `${MONTHS[parseInt(mo, 10) - 1]} ${parseInt(dy, 10)}, ${yr}`;
}

function ScoreCell({ score }) {
  if (score === null || score === undefined) return <span style={{ fontFamily: 'var(--font-data)', fontSize: 13, color: 'var(--text-5)' }}>—</span>;
  if (score === 0) return <span style={{ fontFamily: 'var(--font-data)', fontSize: 13, color: 'var(--negative)' }}>0</span>;
  const color = score >= 80 ? 'var(--accent)' : score >= 60 ? 'var(--text-1)' : 'var(--text-2)';
  const weight = score >= 80 ? 600 : 400;
  return <span style={{ fontFamily: 'var(--font-data)', fontSize: 13, color, fontWeight: weight }}>{score}</span>;
}

export default function RipeningPanel({ token }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLeads = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/ripening-leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLeads(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []); // eslint-disable-line

  const today = new Date().toISOString().slice(0, 10);
  const eligible = leads
    .filter(l => l.eligible_after <= today)
    .sort((a, b) => b.eligible_after.localeCompare(a.eligible_after));
  const upcoming = leads
    .filter(l => l.eligible_after > today)
    .sort((a, b) => a.eligible_after.localeCompare(b.eligible_after));

  const renderRow = (lead, dateLabel) => (
    <div key={lead.id} style={{
      display: 'flex', alignItems: 'center', gap: 10,
      minHeight: 40, padding: '0 18px',
      borderBottom: '1px solid var(--line-soft)',
    }}>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <span style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-1)' }}>{lead.name}</span>
        <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 6 }}>
          {[lead.target_company, lead.batch_label].filter(Boolean).join(' · ')}
        </span>
      </span>
      <ScoreCell score={lead.score} />
      <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', minWidth: 130, textAlign: 'right' }}>
        {dateLabel}
      </span>
      <FeedbackControl
        lead={lead}
        token={token}
        onSaved={(verdict) => setLeads(prev =>
          prev.map(l => l.id === lead.id ? { ...l, feedback_verdict: verdict } : l)
        )}
      />
    </div>
  );

  return (
    <div className="r-card" style={{ padding: 0 }}>
      <div style={{ height: 44, display: 'flex', alignItems: 'center', padding: '0 18px', gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>Ripening</span>
        <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)' }}>{leads.length}</span>
      </div>

      {loading && <div style={{ color: 'var(--text-3)', fontSize: 12, padding: '0 18px 14px' }}>Loading…</div>}

      {error && (
        <div style={{ color: 'var(--negative)', fontSize: 12, padding: '0 18px 14px' }}>
          {error}{' '}
          <span style={{ cursor: 'pointer', color: 'var(--accent)', textDecoration: 'underline' }} onClick={fetchLeads}>Retry</span>
        </div>
      )}

      {!loading && !error && (
        <>
          <div style={{ height: 32, display: 'flex', alignItems: 'center', padding: '0 18px', background: 'var(--subbar-bg)' }}>
            <span className="r-eyebrow">Now eligible</span>
            <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)', marginLeft: 6 }}>{eligible.length}</span>
          </div>
          {eligible.length === 0
            ? <div style={{ color: 'var(--text-3)', fontSize: 12, padding: '8px 18px' }}>None</div>
            : eligible.map(l => renderRow(l, `since ${fmtMonYear(l.eligible_after)}`))}

          <div style={{ height: 32, display: 'flex', alignItems: 'center', padding: '0 18px', background: 'var(--subbar-bg)' }}>
            <span className="r-eyebrow">Upcoming</span>
            <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)', marginLeft: 6 }}>{upcoming.length}</span>
          </div>
          {upcoming.length === 0
            ? <div style={{ color: 'var(--text-3)', fontSize: 12, padding: '8px 18px' }}>None</div>
            : upcoming.map(l => renderRow(l, `Eligible ${fmtMonDayYear(l.eligible_after)}`))}
        </>
      )}
    </div>
  );
}
